/* import __COLOCATED_TEMPLATE__ from './seller-profile-background.hbs'; */
import { computed } from '@ember/object';
import { sort } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@ember/component';

const CLASS_NAME = 'SH-c-seller-profile-background';

/**
 * Different counts for the truncation of the description string on different browser sizes.  This is to get
 * multi-line support then adding ellipses rather than just having browser do this on a single line.
 *
 * @type {number}
 */
const DESCRIPTION_CHARACTER_TRUNCATE_COUNT_MOBILE = 50;
const DESCRIPTION_CHARACTER_TRUNCATE_COUNT_MEDIUM_AND_DOWN = 200;
const DESCRIPTION_CHARACTER_TRUNCATE_COUNT_LARGE_AND_UP = 250;

export default Component.extend({
  /**
   * HTML Component properties
   */
  tagName: 'section',
  className: CLASS_NAME,
  classNameBindings: ['_isHiding:hide'],

  errorTracker: service('error-tracker'),

  /** @param {User} user */
  user: null,

  /** @type {Array<String>} Sorting options for equipment. */
  _equipmentSortOptions: ['name'],

  /** @type {Ember.A<Equipment>} Equipment sorted by name (ascending). */
  _equipmentSortedByName: sort('user.profile.equipment', '_equipmentSortOptions'),

  /** @type {String} HTML string representing the conflict zone training for the user. */
  _conflictZoneString: computed(
    'user.profile.{conflictReporting,crFirstAidTraining,crHostileEnvTraining}',
    function () {
      if (!this.user.profile.conflictReporting) return '';

      const conflictZoneArr = [];

      if (this.user.profile.crHostileEnvTraining) {
        conflictZoneArr.push('Hostile environment training');
      }

      if (this.user.profile.crFirstAidTraining) {
        conflictZoneArr.push('First aid training');
      }

      return conflictZoneArr.join(' • ');
    },
  ),

  /**
   * @type {Boolean}    True when we should hide this component completely.
   */
  _isHiding: computed(
  '_conflictZoneString',
  'user.description',
  'user.profile.equipment.[]',
  '_languageString',
  '_interestsString',
  function() {
    let description = this.get('user.description'),
      languageString = this.get('_languageString'),
      interestsString = this.get('_interestsString'),
      hasProfileEquipment = this.get('user.profile.equipment.length'),
      conflictZoneString = this.get('_conflictZoneString');

    return !description && !languageString && !interestsString && !hasProfileEquipment && !conflictZoneString;
  }),

  /**
   * The description text string will be truncated or not based on state.
   *
   * NOTE - THIS IS NOT HTML ESCAPED, so be on the lookout for HTML injection.
   *
   * @type {String}
   */
  _teaserString: computed(
  '_conflictZoneString',
  'user.description',
  'user.profile.equipment.[]',
  '_languageString',
  '_interestsString',
  '_isBackgroundFullyExpanded',
  'media.isMediumAndDown',
  'media.isMobile',
  function() {
    if (!this.get('user')) {
      return '';
    }

    let description = this.get('user.description'),
      isBackgroundFullyExpanded = this.get('_isBackgroundFullyExpanded'),
      isMobile = this.get('media.isMobile'),
      isMediumAndDown = this.get('media.isMediumAndDown');

    if (isBackgroundFullyExpanded) {
      //background is fully expanded, so this will just be the description as the first string.
      return description;
    }

    // the background is NOT expanded, so we need to pick the teaser string that will show up based on precedence of
    // availability
    let teaserString = '',
      languageString = this.get('_languageString'),
      interestsString = this.get('_interestsString'),
      hasProfileEquipment = this.get('user.profile.equipment.length'),
      conflictZoneString = this.get('_conflictZoneString');
    if (description) {
      teaserString = description;
    } else if (languageString) {
      teaserString = languageString;
    } else if (interestsString) {
      teaserString = interestsString;
    } else if (hasProfileEquipment) {
      let equipment = this.get('user.profile.equipment'),
        stopIndex = equipment.get('length') - 1,
        equipmentString = equipment.reduce((prevString, equipment, index) => {
          if (index === stopIndex) {
            return prevString + equipment.get('name');
          }

          return prevString + equipment.get('name') + ', ';
        }, '');
        teaserString = equipmentString;
    } else if (conflictZoneString) {
      teaserString = conflictZoneString;
    } else {
      return '';
    }

    if (isMobile) {
      return teaserString.length < DESCRIPTION_CHARACTER_TRUNCATE_COUNT_MOBILE ?
        teaserString :
        teaserString.substr(0, DESCRIPTION_CHARACTER_TRUNCATE_COUNT_MOBILE) + '...';
    } else if (isMediumAndDown) {
      return teaserString.length < DESCRIPTION_CHARACTER_TRUNCATE_COUNT_MEDIUM_AND_DOWN ?
        teaserString :
        teaserString.substr(0, DESCRIPTION_CHARACTER_TRUNCATE_COUNT_MEDIUM_AND_DOWN) + '...';
    }

    return teaserString.length < DESCRIPTION_CHARACTER_TRUNCATE_COUNT_LARGE_AND_UP ?
      teaserString :
      teaserString.substr(0, DESCRIPTION_CHARACTER_TRUNCATE_COUNT_LARGE_AND_UP) + '...';
  }),

  /**
   * @type {String}     Based on the precedence of the teaser to show, show the correct heading.
   */
  _teaserTitle: computed(
  '_teaserString',
  '_conflictZoneString',
  'user.description',
  'user.profile.equipment.[]',
  '_languageString',
  '_interestsString',
  function() {
    let description = this.get('user.description'),
      languageString = this.get('_languageString'),
      interestsString = this.get('_interestsString'),
      hasProfileEquipment = this.get('user.profile.equipment.length'),
      conflictZoneString = this.get('_conflictZoneString');

    if (description) {
      return 'Background';
    } else if (languageString) {
      return 'Languages';
    } else if (interestsString) {
      return 'Interests';
    } else if (hasProfileEquipment) {
      return 'Equipment';
    } else if (conflictZoneString) {
      return 'Training';
    }

    //we have a profile with nothing, this should never happen.
    this.get('errorTracker').warn(`_teaserTitle -- Profile with no details for profile id ${this.get('user.id')}`, this);
    return '';
  }),

  /** @type {Ember.ComputedProperty} true if we should show the "show more" link **/
  shouldShowMore: computed(
    '_teaserString',
    'user.description',
    'user.tags.[]',
    'user.profile.languages.[]',
    'user.profile.equipment.[]',
    '_conflictZoneString',
    function() {
      const count = [
        'user.tags',
        'user.description',
        'user.profile.languages',
        'user.profile.equipment',
        '_conflictZoneString',
      ].reduce((accum, prop) => {
        return (this.get(`${prop}.length`) > 0) ? accum + 1 : accum;
      }, 0);

      // if we have more than 1 populated field, or we have truncated the teaser string
      return count > 1 || (this.get('_teaserString').substr(-3) === '...');
    }
  ),

  /** @type {String} HTML representing the interests. */
  _interestsString: computed('user.tags.[]', function () {
    const tags = this.user.tags;

    if (!tags || !tags.length) return '';

    const tagsString = tags.reduce((joinedString, tag, index) => {
      if (index === tags.length - 1) {
        return joinedString += tag.name;
      }

      return joinedString += `${tag.name} • `;
    }, '');

    return tagsString;
  }),

  /**
   * @type {Boolean}      True when we're showing the expanded version, false otherwise
   */
  _isBackgroundFullyExpanded: false,

  /** @type {String} HTML representing the languages. */
  _languageString: computed('user.profile.languages.[]', function () {
    const languages = this.user.profile.languages;

    if (!languages || !languages.length) return '';

    const languageString = languages.reduce((joinedString, language, index) => {
      if (index === languages.length - 1) {
        return joinedString += language.name;
      }

      return joinedString += `${language.name} • `;
    }, '');

    return languageString;
  }),

  actions: {
    /**
     * When user clicks to show less
     */
    onShowLessClicked: function(e) {
      e.stopPropagation();
      e.preventDefault();

      this.set('_isBackgroundFullyExpanded', false)
    },

    /**
     * When user clicks to show more
     */
    onShowMoreClicked: function(e) {
      e.stopPropagation();
      e.preventDefault();

      this.set('_isBackgroundFullyExpanded', true)
    }
  }
});
